import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import hosys from "@/api/api";
import { Sparkles, Loader2, TrendingUp, AlertTriangle, Lightbulb } from "lucide-react";

const CAT_LABELS = {
  starter: "Entrant", main: "Principal", dessert: "Postres",
  drink: "Beguda", side: "Guarnició", sauce: "Salsa", bread: "Pa", other: "Altres",
};

function getMarginColor(margin) {
  if (margin >= 65) return "text-emerald-600";
  if (margin >= 45) return "text-amber-600";
  return "text-red-600";
}

export default function RecipeAIAdvisor({ recipe, onClose }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  const margin = recipe.selling_price > 0
    ? ((recipe.selling_price - recipe.estimated_cost) / recipe.selling_price * 100)
    : 0;
  const grossMargin = recipe.selling_price - recipe.estimated_cost;

  useEffect(() => {
    analyzeRecipe();
  }, [recipe.id]);

  const analyzeRecipe = async () => {
    setLoading(true);
    setAnalysis(null);
    try {
      const ingredientsWithCost = (recipe.ingredients || [])
        .filter(i => i.name)
        .map(i => {
          const cost = (parseFloat(i.unit_cost) || 0) * (parseFloat(i.quantity) || 0);
          const pct = recipe.estimated_cost > 0 && cost > 0 ? (cost / recipe.estimated_cost * 100).toFixed(0) : null;
          return `${i.name}: ${i.quantity}${i.unit}${cost > 0 ? ` (${cost.toFixed(2)}€${pct ? `, ${pct}% del cost total` : ""})` : ""}`;
        })
        .join("\n- ");

      const prompt = `Analitza la rendibilitat d'aquest plat de restaurant i dona suggeriments concrets i accionables:

Plat: ${recipe.name}
Categoria: ${CAT_LABELS[recipe.category] || recipe.category}
Cost del plat: ${recipe.estimated_cost?.toFixed(2)}€
Preu de venda: ${recipe.selling_price?.toFixed(2)}€
Marge brut: ${grossMargin.toFixed(2)}€ (${margin.toFixed(1)}%)
${ingredientsWithCost ? `Ingredients amb cost:\n- ${ingredientsWithCost}` : "Ingredients: No especificats"}

Retorna un JSON amb aquesta estructura exacta:
{
  "summary": "Resum breu de la situació de rendibilitat (1-2 frases)",
  "margin_assessment": "bo|acceptable|baix|crític",
  "suggestions": [
    {
      "type": "pricing|ingredients|cost|recipe",
      "title": "Títol curt del suggeriment",
      "detail": "Explicació concreta i accionable (màx 2 frases)"
    }
  ],
  "key_insight": "La observació més important per millorar la rendibilitat"
}

Sigues específic i pràctic. Màxim 3 suggeriments. En català.`;

      const result = await hosys.integrations.Core.InvokeLLM({
        prompt,
        response_json_schema: {
          type: "object",
          properties: {
            summary: { type: "string" },
            margin_assessment: { type: "string" },
            suggestions: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  type: { type: "string" },
                  title: { type: "string" },
                  detail: { type: "string" },
                }
              }
            },
            key_insight: { type: "string" },
          }
        }
      });
      setAnalysis(result);
    } catch {
      setAnalysis({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const typeIcons = {
    pricing: TrendingUp,
    ingredients: AlertTriangle,
    cost: AlertTriangle,
    recipe: Lightbulb,
  };

  const typeColors = {
    pricing: "bg-blue-50 text-blue-600 border-blue-100",
    ingredients: "bg-orange-50 text-orange-600 border-orange-100",
    cost: "bg-red-50 text-red-600 border-red-100",
    recipe: "bg-violet-50 text-violet-600 border-violet-100",
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#7B2D3F]" />
            Anàlisi IA — {recipe.name}
          </DialogTitle>
        </DialogHeader>

        {/* Cost summary */}
        <div className="grid grid-cols-3 gap-2 mb-1">
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-[10px] text-gray-400 font-semibold uppercase">Cost</p>
            <p className="text-base font-bold text-gray-800">{recipe.estimated_cost?.toFixed(2)}€</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-[10px] text-gray-400 font-semibold uppercase">PVP</p>
            <p className="text-base font-bold text-gray-800">{recipe.selling_price?.toFixed(2)}€</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-[10px] text-gray-400 font-semibold uppercase">Marge</p>
            <p className={`text-base font-bold ${getMarginColor(margin)}`}>{margin.toFixed(1)}%</p>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-[#7B2D3F]" />
            <p className="text-sm text-gray-500">Analitzant el plat...</p>
          </div>
        ) : analysis?.error ? (
          <p className="text-sm text-red-600 text-center py-4">Error generant l'anàlisi. Torna-ho a intentar.</p>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-[#f9f0f2] rounded-xl p-4">
              <p className="text-sm text-gray-700 leading-relaxed">{analysis?.summary}</p>
            </div>

            {/* Key insight */}
            {analysis?.key_insight && (
              <div className="flex gap-2.5 bg-amber-50 border border-amber-100 rounded-xl p-3.5">
                <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed font-medium">{analysis.key_insight}</p>
              </div>
            )}

            {/* Suggestions */}
            {analysis?.suggestions?.length > 0 && (
              <div className="space-y-2">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Suggeriments</p>
                {analysis.suggestions.map((s, i) => {
                  const Icon = typeIcons[s.type] || Lightbulb;
                  const colorClass = typeColors[s.type] || "bg-gray-50 text-gray-600 border-gray-100";
                  return (
                    <div key={`step-${i}`} className={`border rounded-xl p-3.5 ${colorClass}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                        <p className="text-xs font-bold">{s.title}</p>
                      </div>
                      <p className="text-xs leading-relaxed opacity-80">{s.detail}</p>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-[10px] text-gray-400 text-center">
              Suggeriments orientatius · La decisió final és teva
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}